import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getAuthToken } from '../utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Extract raw JWT from "Bearer <token>" string */
function getRawToken() {
  const auth = getAuthToken();
  return auth?.startsWith('Bearer ') ? auth.slice(7) : auth || '';
}

/**
 * useGameIntelligence — Fetches games from the game-intelligence API
 * and provides SSE progress tracking for active pipelines.
 *
 * @param {string} gameSet - Filter games by gameSet (optional)
 * @returns {{ games, loading, error, refreshGames, pipelineProgress }}
 */
export function useGameIntelligence(gameSet = null) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pipelineProgress, setPipelineProgress] = useState({});
  const sseRefs = useRef({});

  const fetchGames = useCallback(async () => {
    try {
      const qs = gameSet ? `?gameSet=${encodeURIComponent(gameSet)}` : '';
      const headers = {};
      const authToken = getAuthToken();
      if (authToken) headers.Authorization = authToken;
      const res = await fetch(`${BACKEND_URL}/api/game-intelligence/games${qs}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setGames(data.games);
      }
      setError(null);
    } catch (err) {
      console.error('[useGameIntelligence] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [gameSet]);

  // Initial load
  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // Derive stable key for SSE subscriptions (avoids reconnecting on every render)
  const activeGameIds = useMemo(
    () => games
      .filter(g => ['segmenting', 'uploading_segments', 'analyzing', 'pending'].includes(g.status))
      .map(g => g.id)
      .sort()
      .join(','),
    [games]
  );

  // Subscribe to SSE progress for in-progress games
  useEffect(() => {
    const activeIds = activeGameIds ? activeGameIds.split(',') : [];

    // Close stale SSE connections
    Object.keys(sseRefs.current).forEach(gameId => {
      if (!activeIds.includes(gameId)) {
        sseRefs.current[gameId]?.close();
        delete sseRefs.current[gameId];
      }
    });

    // Open SSE for active pipelines
    activeIds.forEach(gameId => {
      if (sseRefs.current[gameId]) return; // already connected

      const token = getRawToken();
      const sseUrl = `${BACKEND_URL}/api/game-intelligence/progress/${gameId}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      const es = new EventSource(sseUrl);
      sseRefs.current[gameId] = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'complete') {
            fetchGames();
            es.close();
            delete sseRefs.current[gameId];
          } else if (data.type === 'error') {
            setPipelineProgress(prev => ({
              ...prev,
              [gameId]: { ...data, phase: 'error' },
            }));
            es.close();
            delete sseRefs.current[gameId];
          } else {
            setPipelineProgress(prev => ({
              ...prev,
              [gameId]: data,
            }));
          }
        } catch { /* ignore parse errors */ }
      };

      es.onerror = () => {
        es.close();
        delete sseRefs.current[gameId];
      };
    });

    return () => {
      Object.values(sseRefs.current).forEach(es => es.close());
      sseRefs.current = {};
    };
  }, [activeGameIds, fetchGames]);

  return {
    games,
    loading,
    error,
    refreshGames: fetchGames,
    pipelineProgress,
  };
}

/**
 * Convert API game object to the format expected by BrownHarvardDashboard.
 * Bridges the gap between the JSON registry format and the hardcoded GAMES array format.
 */
export function apiGameToDashboardGame(apiGame) {
  const isLegacyBrownHarvard = apiGame.id === 'BROWN_AT_HARVARD_01.30.26';
  const folder = isLegacyBrownHarvard
    ? 'brown-harvard-2026-01-30'
    : apiGame.id;

  // Format date for display
  let displayDate = apiGame.date;
  try {
    const d = new Date(apiGame.date + 'T00:00:00');
    displayDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { /* keep raw */ }

  return {
    id: apiGame.id,
    name: apiGame.name,
    date: displayDate,
    folder,
    segments: apiGame.segments || 0,
    duration: apiGame.duration || 0,
    segmentDuration: apiGame.segmentDuration || 60,
    gameSet: apiGame.gameSet || '',
    status: apiGame.status || 'complete',
    synergyOnly: apiGame.status === 'synergy_only' || (apiGame.segments === 0 && apiGame.status !== 'pending'),
    videoPath: (seg) => {
      if (isLegacyBrownHarvard) {
        return `segments/brown-harvard-2026-01-30/plays/${seg + 1}.mp4`;
      }
      const prefix = apiGame.gcsSegmentPrefix || `segments/${apiGame.gameSet}/${apiGame.id}/`;
      return `${prefix}seg_${String(seg).padStart(4, '0')}.mp4`;
    },
  };
}
